// Session change journal (PLAN-agent-v2 #7): before-image of every range a write tool touches
// (values, formulas, number formats) so the user — or the agent via `excel_undo_last_change` —
// can revert. Pure data structure; restoring goes through the executor (the Office.js seam).

export interface RangeSnapshot {
  sheet: string;
  address: string;
  values: unknown[][];
  /** Office.js `formulas`: the formula string for formula cells, the plain value otherwise. */
  formulas: unknown[][];
  numberFormats: string[][];
}

export interface JournalEntry {
  id: string;
  /** Tool that produced the change (e.g. excel_write_range). */
  tool_name: string;
  tool_call_id: string;
  /** Human-readable summary (the server preview when available). */
  summary: string;
  before: RangeSnapshot;
  /** Further areas of a multi-area write ("D13,D42,D43"): restored together with `before`. */
  extra?: RangeSnapshot[];
  timestamp: string;
  /** Entries already reverted stay in the log for the agent's benefit. */
  undone: boolean;
  /** True when the entry can no longer be restored (its sheet was deleted): undone without a restore. */
  invalidated?: boolean;
}

export interface JournalEntryInput {
  tool_name: string;
  tool_call_id: string;
  summary: string;
  before: RangeSnapshot;
  extra?: RangeSnapshot[];
}

let seq = 0;
/** Ids are unique per pane load, not per session only: the server files a before-image under (user, id) and
 *  REPLACES the one already there, so `j1` restarting from zero at every load overwrote earlier threads'
 *  images (seen 2026-09-24: today's 30 entries took the place of 17 September's, dates included). */
const LOAD = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export class ChangeJournal {
  private entries: JournalEntry[] = [];

  constructor(private readonly maxEntries = 200) {}

  record(input: JournalEntryInput): JournalEntry {
    const entry: JournalEntry = { id: `${LOAD}-j${++seq}`, timestamp: new Date().toISOString(), undone: false, ...input };
    this.entries.push(entry);
    if (this.entries.length > this.maxEntries) this.entries.splice(0, this.entries.length - this.maxEntries);
    return entry;
  }

  /** Most recent entries first (what `excel_get_change_journal` returns). */
  list(lastN = 20): JournalEntry[] {
    return this.entries.slice(-lastN).reverse();
  }

  get(id: string): JournalEntry | undefined {
    return this.entries.find((e) => e.id === id);
  }

  /** The `count` most recent NOT-yet-undone entries, newest first (undo order). */
  undoCandidates(count = 1): JournalEntry[] {
    const out: JournalEntry[] = [];
    for (let i = this.entries.length - 1; i >= 0 && out.length < count; i--) {
      if (!this.entries[i].undone) out.push(this.entries[i]);
    }
    return out;
  }

  markUndone(id: string, invalidated = false): void {
    const e = this.get(id);
    if (!e) return;
    e.undone = true;
    if (invalidated) e.invalidated = true;
  }

  /** A sheet was deleted: its entries can never be restored → mark them undone + invalidated. */
  invalidateSheet(sheet: string): number {
    let n = 0;
    for (const e of this.entries) {
      if (e.before.sheet === sheet && !e.undone) {
        e.undone = true;
        e.invalidated = true;
        n++;
      }
    }
    return n;
  }

  /** A sheet was renamed: keep its entries undoable under the new name. */
  renameSheet(from: string, to: string): number {
    let n = 0;
    for (const e of this.entries) {
      if (e.before.sheet === from) {
        e.before.sheet = to;
        for (const x of e.extra ?? []) x.sheet = to;
        n++;
      }
    }
    return n;
  }

  size(): number {
    return this.entries.length;
  }

  clear(): void {
    this.entries = [];
  }
}

/** Every snapshot an entry restores (first area + extra areas). */
export function entrySnapshots(e: JournalEntry): RangeSnapshot[] {
  return [e.before, ...(e.extra ?? [])];
}

export const changeJournal = new ChangeJournal();
